import NextImage from 'next/image'
import { useTranslation } from '@mc/i18n'
import { cloudflareLoader } from '@mc/image-cloudflare'
import { SectionHeading } from './SectionHeading'

type SolutionImage = {
  src: string
  alt: string
}

const MASTERCLASS_TILES: SolutionImage[] = [
  {
    src: 'https://static.masterclass.com/Carousel_Joanna_Gaines_4.jpg',
    alt: '',
  },
  {
    src: 'https://static.masterclass.com/Hero_Carousel_Kevin_Hart.jpg',
    alt: '',
  },
  { src: 'https://static.masterclass.com/Carousel_Gordon_Ramsay.jpg', alt: '' },
]

const AT_WORK_IMAGE: SolutionImage[] = [
  {
    src: 'https://static.masterclass.com/for-business-solutions-maw.jpg',
    alt: '',
  },
]

const CERTIFICATES_IMAGE: SolutionImage[] = [
  {
    src: 'https://static.masterclass.com/get_this_certificate_nsf_1x1.jpg',
    alt: '',
  },
]

// A collage tile is a third of a card, so it needs a third of the card's sizes.
const COLLAGE_TILE_SIZES =
  '(max-width: 767px) 33vw, (max-width: 1263px) 11vw, 133px'
const FULL_CARD_SIZES =
  '(max-width: 767px) 96vw, (max-width: 1263px) 32vw, 406px'

const SolutionCard = ({
  title,
  description,
  images,
}: {
  title: string
  description: string
  images: SolutionImage[]
}) => (
  <div className='col-12 col-md-4 mc-mb-8 mc-mb-md-0'>
    <div
      className='d-flex mc-gap-1 mc-corners--md mc-overflow--hidden position-relative'
      style={{ aspectRatio: '385 / 240' }}
    >
      {images.map((image) => (
        <div className='position-relative flex-1' key={image.src}>
          <NextImage
            src={image.src}
            alt={image.alt}
            loader={cloudflareLoader}
            fill
            sizes={images.length > 1 ? COLLAGE_TILE_SIZES : FULL_CARD_SIZES}
            style={{ objectFit: 'cover' }}
          />
        </div>
      ))}
    </div>
    <h3 className='mc-font-headline3 mc-mt-6 mc-mb-4'>{title}</h3>
    <p className='mc-text-color--tint'>{description}</p>
  </div>
)

export const SolutionsSection = () => {
  const { t } = useTranslation('@mc/next')

  const solutions = [
    {
      title: t('forBusiness.solutionsSection.masterClass.title'),
      description: t('forBusiness.solutionsSection.masterClass.description'),
      images: MASTERCLASS_TILES,
    },
    {
      title: t('forBusiness.solutionsSection.masterClassAtWork.title'),
      description: t(
        'forBusiness.solutionsSection.masterClassAtWork.description',
      ),
      images: AT_WORK_IMAGE,
    },
    {
      title: t('forBusiness.solutionsSection.certificates.title'),
      description: t('forBusiness.solutionsSection.certificates.description'),
      images: CERTIFICATES_IMAGE,
    },
  ]

  return (
    <section className='mc-my-10'>
      <SectionHeading label={t('forBusiness.solutionsSection.label')}>
        <>
          {t('forBusiness.solutionsSection.heading')}
          <br />
          {t('forBusiness.solutionsSection.headingSecondLine')}
        </>
      </SectionHeading>
      <div className='row mc-mt-10'>
        {solutions.map((solution) => (
          <SolutionCard key={solution.title} {...solution} />
        ))}
      </div>
    </section>
  )
}
